import { Box, Button, Card, Grid, Toolbar, Typography } from '@material-ui/core'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import arrayMutators from 'final-form-arrays'
import { isEmpty } from 'lodash'
import {
  ArrayInput,
  Error,
  FunctionField,
  Loading,
  required,
  SelectInput,
  SimpleFormIterator,
  TextInput,
  useGetOne,
  useNotify,
  useRedirect,
} from 'react-admin'
import { Form } from 'react-final-form'
import { ConfirmDialog } from 'shared/components/Dialog/ConfirmDialog'

import { captureExceptionIfProduction, getBrokerReferenceFilter } from 'helpers/utils'
import { generateInvoice } from '../../api/jobs'
import { INVOICE_CORRECTION_REASON_CHOICES, STATEMENT_LINE_ITEM_TYPES } from '../../helpers/constants'
import { validateInvoiceItems, validateNonZeroNumber, validateTwoDecimals } from '../../helpers/validators'
import { BrokerReferenceInput, BrokerReferenceInputClassName } from '../Common/Brokers'
import { LabeledTypographyField, SimpleNumberInput } from '../Fields'

let getTotal = (record: any) => {
  let total = 0.0
  for (let item of record.items) {
    if (item) {
      total += parseFloat(item.amount)
    }
  }

  if (!total) {
    total = 0.0
  }

  return total
}

const validateAmount = [required(), validateNonZeroNumber, validateTwoDecimals]

const GenerateInvoiceForm = ({ match }: any) => {
  const redirectTo = useRedirect()
  const notify = useNotify()
  const { id, verifyDelivery: shouldVerifyDelivery } = match.params
  const { data: job, loading, error } = useGetOne('jobs', id)
  if (loading) {
    return <Loading />
  }
  if (error) {
    return <Error error={error} />
  }
  let jobStatement = job?.statements[0]

  const dialogTitle = shouldVerifyDelivery ? 'Verify Delivery & Create New Invoice' : 'Create New Invoice'
  const dialogContent = shouldVerifyDelivery
    ? `You are about to verify delivery and create a new invoice for job ${job?.human_id}.`
    : `You are about to create a new invoice for job ${job?.human_id}.`
  const hasInvoices = !isEmpty(job?.invoices)

  let initialValues: any = {
    items: jobStatement.line_items.map((value: any) => {
      let description
      if (value.type === STATEMENT_LINE_ITEM_TYPES.loadPrice) {
        description = `Contracted price of job with Broker Reference # - ${job?.load.source_human_id}`
      } else {
        description = value.type
      }
      return {
        amount: value.amount,
        description: description,
      }
    }),
  }

  if (shouldVerifyDelivery) {
    initialValues.statement = jobStatement.id
  }

  const broker_id = job?.load?.broker?.id
  if (broker_id) {
    initialValues.broker_id = broker_id
  }
  const brokerReferenceInputFilters = getBrokerReferenceFilter(job?.team?.is_premium)

  function handleConfirm(values: any) {
    let invoice_correction_reason = INVOICE_CORRECTION_REASON_CHOICES.find(
      (value) => values.invoice_correction_reason !== 'OTHER' && value.id === values.invoice_correction_reason
    )
    if (invoice_correction_reason) {
      values.internal_notes = invoice_correction_reason.name
    }
    return generateInvoice(id, values, shouldVerifyDelivery)
      .then(() => {
        notify('Invoice generated for job!', { type: 'success' })
        redirectTo(`/jobs/${id}/show/invoices`)
      })
      .catch((error) => {
        // These errors are race conditions that occur when a similar invoice is created.
        const isErrorInvoiceExists = (error: any) => {
          if (!error.response.data) return false
          if (error.response.data === 'Transition not allowed for this job') return true
          if (error.response.data[0] === 'This invoice is currently being created by someone else.')
            return true
          if (
            error.response.data['statement'] &&
            error.response.data['statement'][0] === 'This field must be unique.'
          )
            return true
          return false
        }
        if (isErrorInvoiceExists(error)) {
          notify('Invoice was already created!', { type: 'error' })
        } else {
          notify(`${error.response.statusText}: ${JSON.stringify(error.response.data)}`, { type: 'error' })
          captureExceptionIfProduction(error)
        }
      })
  }

  return (
    <>
      <Card style={{ padding: 20 }}>
        <Grid container direction="column" justifyContent="flex-start" alignItems="flex-start" spacing={2}>
          <Grid item>
            <Typography component="span" variant="h5" color="textPrimary">
              Generate invoice for Job: {job?.human_id}
            </Typography>
          </Grid>
          <Grid item>
            <Box p="1em">
              <Box display="flex">
                <Form
                  initialValues={initialValues}
                  onSubmit={() => {}}
                  validate={validateInvoiceItems}
                  mutators={{ ...arrayMutators }}
                  subscription={defaultSubscription}
                  keepDirtyOnReinitialize
                  render={(formProps) => {
                    const { values } = formProps
                    return (
                      <form>
                        <Box p="1em">
                          {job?.invoices.length > 0 && (
                            <SelectInput
                              fullWidth={true}
                              label="Invoice Correction Reason"
                              source="invoice_correction_reason"
                              validate={required()}
                              choices={INVOICE_CORRECTION_REASON_CHOICES}
                            />
                          )}
                          {values.invoice_correction_reason === 'OTHER' && (
                            <TextInput
                              source="internal_notes"
                              label="Reason Note"
                              validate={required()}
                              fullWidth
                            />
                          )}
                          <Box display="flex">
                            <Box flex={1} mr="1em" width={500}>
                              {initialValues.broker_id ? (
                                <TextInput
                                  source="broker_id"
                                  label="Broker"
                                  disabled
                                  format={() => job?.load.broker.name}
                                />
                              ) : (
                                <BrokerReferenceInput
                                  source="broker_id"
                                  label="Broker"
                                  reference="brokers"
                                  filter={brokerReferenceInputFilters}
                                  filterToQuery={(searchText: any) => ({ search: searchText })}
                                  fullWidth={true}
                                  validate={required()}
                                  containerClassName={BrokerReferenceInputClassName.invoice}
                                />
                              )}
                            </Box>
                          </Box>
                          <Box display="flex">
                            <Box flex={1}>
                              <ArrayInput source="items">
                                <SimpleFormIterator>
                                  <TextInput
                                    source="description"
                                    fullWidth
                                    multiline
                                    label="Description"
                                    validate={required()}
                                  />
                                  <SimpleNumberInput
                                    source="amount"
                                    label="Amount"
                                    validate={validateAmount}
                                  />
                                </SimpleFormIterator>
                              </ArrayInput>
                            </Box>
                          </Box>
                          <Box display="flex">
                            <Box flex={1}>
                              <FunctionField
                                label="Total"
                                record={formProps.values}
                                render={(record: any) => {
                                  let total = getTotal(record)
                                  return (
                                    <LabeledTypographyField
                                      value={`$${total}`}
                                      label="Total"
                                      component="span"
                                      variant="body1"
                                    />
                                  )
                                }}
                              />
                            </Box>
                          </Box>
                          <Toolbar>
                            <Box display="flex" justifyContent="space-between" width="100%">
                              <ConfirmDialog
                                handleConfirm={() => handleConfirm(formProps.values)}
                                handleError={() => {
                                  if (getTotal(formProps.values) <= 0) {
                                    notify('The sum of the amounts must be greater than zero.', {
                                      type: 'error',
                                    })
                                  }
                                }}
                                dialogContent={
                                  <>
                                    <Typography variant="h4" align="center">
                                      {dialogTitle}
                                    </Typography>
                                    <DialogContent>
                                      <DialogContentText>{dialogContent}</DialogContentText>
                                      {hasInvoices && (
                                        <>
                                          <DialogContentText>
                                            WARNING: This will automatically cancel (with a credit memo) any
                                            existing invoices.
                                          </DialogContentText>
                                          <DialogContentText>
                                            In the (rare) case you'd prefer this to not happen, please reach
                                            out to accounting@cloudtrucks.com instead.
                                          </DialogContentText>
                                        </>
                                      )}
                                    </DialogContent>
                                  </>
                                }
                                confirmLabel="Confirm"
                                cancelLabel="Cancel"
                              >
                                <Button variant="contained" color="primary">
                                  Create Invoice
                                </Button>
                              </ConfirmDialog>
                            </Box>
                          </Toolbar>
                        </Box>
                      </form>
                    )
                  }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Card>
    </>
  )
}

const defaultSubscription = {
  submitting: true,
  pristine: true,
  valid: true,
  dirty: false,
  invalid: true,
  values: true,
}

export default GenerateInvoiceForm
